import { ComponentProps, ComponentType, FC, ReactNode, useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider as BaseQueryClientProvider } from 'react-query'
import { fireEvent } from 'utilities/analytics'
import type { ResponseError } from 'api'
import { useAuth } from 'react-oidc-context'
import { SitesApi } from '@trybeapp/sdk'
import { execute } from 'utilities/Function'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const sites = new SitesApi(apiConfiguration)

/**
 * Have to manually fetch flags as the SDK and react-query won't have been configured.
 *
 * This can be deleted once the `logout_all_users` feature is globally enabled.
 */
function useFeatureFlags() {
  const [flags, setFlags] = useState<string[]>()
  const siteId = sessionStorage.getItem('activeSiteId')

  useEffect(() => {
    if (!siteId) return

    const request = new AbortController()

    void execute(async () => {
      const { data } = await sites.getSiteFlags({ siteId }, { signal: request.signal })

      setFlags(data)
    })

    return () => request.abort()
  }, [siteId])

  return flags
}

interface Props {
  children: ReactNode
}

export const QueryClientProvider: FC<Props> = ({ children }) => {
  const { signoutRedirect } = useAuth()
  const flags = useFeatureFlags()

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          mutations: {
            onError: (error) => {
              const event = getEventFromError(error)
              fireEvent('mutation_error', event)

              if (event.status === 401 && flags?.includes('logout_all_users')) {
                signoutRedirect()
              }
            },
          },
          queries: {
            onError: (error: ResponseError) => {
              const event = getEventFromError(error)
              fireEvent('query_error', event)

              if (event.status === 401 && flags?.includes('logout_all_users')) {
                signoutRedirect()
              }
            },
            // By default, if any request returns a 401, 422, 403 or 404, don't retry.
            // Any other status code will retry 2 more times.
            retry: (failureCount, error: ResponseError) => {
              const response = error instanceof Response ? error : error.response
              const status = response?.status ?? 400

              if ([401, 422, 403, 404].includes(status)) {
                return false
              }

              return failureCount < 2
            },
          },
        },
      })
  )

  return <BaseQueryClientProvider client={client}>{children}</BaseQueryClientProvider>
}

export function withQueryClient<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <QueryClientProvider>
        <Component {...props} />
      </QueryClientProvider>
    )
  }
}

function getEventFromError(error: unknown) {
  const response =
    error && typeof error === 'object' && 'response' in error
      ? error instanceof Response
        ? error
        : (error as Exclude<ResponseError, Response>).response
      : null
  const [url] = response
    ? ('error' in response ? response.error : response).url.split('?') ?? []
    : []

  return { status: response?.status, url }
}
